// =============================================================
// Skeleton — shimmer placeholders for loading states. Variants: a text line,
// a card block, and a table row (N cells). Pure presentational + typed; the
// shimmer is a CSS keyframe over the design-system surface tokens. Marked
// aria-hidden + role=presentation so screen readers skip the placeholder.
// =============================================================

import type { CSSProperties } from "react";

export type SkeletonVariant = "text" | "card" | "table-row";

export interface SkeletonProps {
  variant?: SkeletonVariant;
  /** Number of repeats (text lines / table rows / cards). Default 1. */
  count?: number;
  /** For variant="table-row": cells per row. Default 4. */
  cols?: number;
  /** Override width (text/card), e.g. "60%" for a short last line. */
  width?: string;
  /** Override height (card), e.g. 120. */
  height?: number;
  className?: string;
}

/** Animated shimmer placeholders for loading states. */
export function Skeleton({ variant = "text", count = 1, cols = 4, width, height, className = "" }: SkeletonProps) {
  const items = Array.from({ length: Math.max(1, count) });

  if (variant === "table-row") {
    return (
      <>
        {items.map((_, r) => (
          <tr key={r} className="sk-row" aria-hidden="true">
            {Array.from({ length: Math.max(1, cols) }).map((__, c) => (
              <td key={c}>
                <span className="sk sk-line" />
              </td>
            ))}
          </tr>
        ))}
      </>
    );
  }

  if (variant === "card") {
    const style: CSSProperties = { height: height ?? 120, width: width ?? "100%" };
    return (
      <div className={`sk-cards ${className}`.trim()} role="presentation" aria-hidden="true">
        {items.map((_, i) => (
          <span key={i} className="sk sk-card" style={style} />
        ))}
      </div>
    );
  }

  // text
  return (
    <div className={`sk-lines ${className}`.trim()} role="presentation" aria-hidden="true">
      {items.map((_, i) => (
        <span key={i} className="sk sk-line" style={{ width: width ?? (i === items.length - 1 && items.length > 1 ? "60%" : "100%") }} />
      ))}
    </div>
  );
}
